import { useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { notifications } from '@mantine/notifications';
import { orderlinesService } from '../../../services/orderlines/orderlines.service';
import { kdsService } from '../../../services/kds/kds.service';
import { logToBackend } from '../../../services/client-log/client-log.service';
import { captureKdsReceiptSnapshot } from '../../KdsPage/utils/kds-page';
import type { KdsPrintTicketLine } from '../../KdsPage/components/print/KdsPrintTicket';

type Options = {
  tenantId: string;
  orderId: number | undefined;
  employeeSession: string | null;
  isManager: boolean;
  receiptRef: RefObject<HTMLDivElement>;
  createActionGroupId: () => string;
  closeSelection: () => void;
  setBadge: (badge: string) => void;
  setPrinterName: (name: string) => void;
  setPreviewLines: (lines: KdsPrintTicketLine[] | null) => void;
};

const DEFAULT_PRINTER_NAME = 'Kitchen Printer';

const toPreviewLines = (lines: Array<{
  orderlineId: number;
  productName: string;
  quantity: number;
  selectionLabels?: string[] | null;
  selectionGroupName?: string | null;
}>): KdsPrintTicketLine[] => lines.map((line) => ({
  id: line.orderlineId,
  productName: line.productName,
  selectionGroups: line.selectionLabels?.length
    ? [{ groupName: line.selectionGroupName ?? '', options: line.selectionLabels.map((name) => ({ name, extraPrice: 0, quantity: 1 })) }]
    : [],
  quantity: line.quantity,
  note: null,
}));

const waitForNextFrame = (): Promise<void> => new Promise((resolve) => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => resolve());
    return;
  }
  setTimeout(resolve, 0);
});

// Details: docs/function-library/web/contexts-pages.md#order-reprint-diagnostics
export function useOrderReprint(options: Options) {
  const inFlight = useRef(false);
  const [isReprinting, setIsReprinting] = useState(false);

  const handleConfirmReprint = async (selections: Array<{ orderlineId: number; quantity: number }>) => {
    const { tenantId, orderId, employeeSession, isManager, receiptRef } = options;
    if (orderId === undefined || !employeeSession || !isManager || inFlight.current) return;
    inFlight.current = true;
    setIsReprinting(true);
    const startedAt = Date.now();
    let trace: string | undefined;
    let stage = 'initializing';
    let printerId: number | undefined;
    let groupIndex: number | undefined;
    const log = (message: string, data: Record<string, unknown> = {}, level: 'info' | 'warn' | 'error' = 'info') => {
      logToBackend('reprint-trace', message, {
        trace, tenantId, orderId, stage, printerId, groupIndex,
        elapsedMs: Date.now() - startedAt, ...data,
      }, level);
    };
    try {
      trace = options.createActionGroupId();
      stage = 'reprint HTTP request';
      log('reprint HTTP request starting', { selectionCount: selections.length });
      const { printerGroups } = await orderlinesService.reprint(tenantId, orderId, employeeSession, selections, trace, true);
      log('reprint HTTP request succeeded', { printerGroupCount: printerGroups.length });
      options.closeSelection();
      options.setBadge('הדפסה חוזרת');
      let queuedPrinterCount = 0;
      let browserRuntimeSkippedCount = 0;
      for (groupIndex = 0; groupIndex < printerGroups.length; groupIndex += 1) {
        const group = printerGroups[groupIndex];
        printerId = group.printerId;
        stage = 'ticket capture';
        flushSync(() => {
          options.setPrinterName(group.printerName);
          options.setPreviewLines(toPreviewLines(group.lines));
        });
        const captureStartedAt = Date.now();
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
          options.setPrinterName(DEFAULT_PRINTER_NAME);
          options.setPreviewLines(null);
          browserRuntimeSkippedCount += 1;
          log('printer group capture skipped', { captureElapsedMs: Date.now() - captureStartedAt, reason: 'no_browser_runtime' }, 'warn');
          continue;
        }
        await waitForNextFrame();
        log('printer group capture started', { lineCount: group.lines.length, hasRenderNode: Boolean(receiptRef.current) });
        const image = await captureKdsReceiptSnapshot(receiptRef.current);
        if (!image) {
          options.setPrinterName(DEFAULT_PRINTER_NAME);
          options.setPreviewLines(null);
          log('printer group capture produced no image', { captureElapsedMs: Date.now() - captureStartedAt }, 'error');
          notifications.show({ color: 'red', title: 'שגיאה', message: `לא ניתן ליצור תצוגת הדפסה עבור ${group.printerName}. נסה שוב.`, autoClose: 3000 });
          continue;
        }
        log('printer group capture completed', { captureElapsedMs: Date.now() - captureStartedAt });
        stage = 'print queue HTTP request';
        const queueStartedAt = Date.now();
        log('queue HTTP request starting');
        await kdsService.queueOrderPrint(
          tenantId, orderId, group.printerId, employeeSession, undefined,
          group.lines.map((line) => line.orderlineId), trace, group.printerName,
          undefined,
          image, 'reprint',
        );
        queuedPrinterCount += 1;
        log('queue HTTP request succeeded', { queueElapsedMs: Date.now() - queueStartedAt });
      }
      stage = 'settled';
      printerId = undefined;
      groupIndex = undefined;
      const intentionallySkipped = browserRuntimeSkippedCount > 0 && queuedPrinterCount + browserRuntimeSkippedCount === printerGroups.length;
      const complete = queuedPrinterCount === printerGroups.length;
      log(
        intentionallySkipped ? 'reprint flow skipped' : (complete ? 'reprint flow completed' : 'reprint flow incomplete'),
        { printerGroupCount: printerGroups.length, queuedPrinterCount, browserRuntimeSkippedCount },
        intentionallySkipped ? 'warn' : (complete ? 'info' : 'warn'),
      );
      if (complete) {
        notifications.show({ color: 'green', title: 'הדפסה חוזרת נשלחה', message: 'הפריטים שנבחרו נשלחו שוב.', autoClose: 3000 });
      }
    } catch (error) {
      // HTTP rejection details may contain guard outcomes; those stay in backend logs (Rule 33).
      log('reprint flow failed', stage === 'ticket capture' && error instanceof Error
        ? { message: error.message, stack: error.stack }
        : {}, 'error');
      notifications.show({ color: 'red', title: 'שגיאה', message: 'ההדפסה החוזרת נכשלה. נסה שוב.', autoClose: 3000 });
    } finally {
      options.setBadge('חדש');
      options.setPrinterName(DEFAULT_PRINTER_NAME);
      options.setPreviewLines(null);
      inFlight.current = false;
      setIsReprinting(false);
    }
  };

  return { isReprinting, handleConfirmReprint };
}
